import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { ImageUploadHelper } from "../src/image_upload_helper"

export default class extends Controller {
    static targets = ["fileInput", "preview", "status", "submitButton", "selectButton"]
    static values = { 
        directUploadUrl: String,
        maxFileSize: { type: Number, default: 10485760 } // 10MB
    }

    connect() {
        this.handleFileSelection = this.handleFileSelection.bind(this)
        this.reset = this.reset.bind(this)
        this.originalPreviewUrl = this.previewTarget.querySelector("img")?.src
        this.form = this.element.closest("form")

        this.fileInputTarget.addEventListener("change", this.handleFileSelection)
        this.form.addEventListener("reset", this.reset)
    }

    disconnect() {
        this.fileInputTarget.removeEventListener("change", this.handleFileSelection)
        this.form.removeEventListener("reset", this.reset)
    }

    reset() {
        const img = this.previewTarget.querySelector("img")
        if (img) {
            img.src = this.originalPreviewUrl
            img.style.opacity = "1"
        }

        this.element.querySelectorAll('input[name="user[avatar]"][type="hidden"]').forEach((input) => input.remove())
        this.statusTarget.classList.add("hidden")
        this.selectButtonTarget.textContent = "Change photo"
        this.selectButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }

    handleFileSelection(event) {
        const file = event.target.files[0]
        
        if (!file) {
            return
        }

        // Validate file
        if (!this.validateFile(file)) {
            this.resetFileInput()
            return
        }

        // Show preview and start upload
        this.showPreview(file)
        this.uploadFile(file)
    }

    validateFile(file) {
        // Check file type
        if (!file.type.startsWith('image/')) {
            this.showError("Please select an image file")
            return false
        }

        // Check file size
        if (file.size > this.maxFileSizeValue) {
            const maxSizeMB = this.maxFileSizeValue / 1024 / 1024
            this.showError(`File size must be less than ${maxSizeMB}MB`)
            return false
        }

        return true
    }

    showPreview(file) {
        const reader = new FileReader()
        
        reader.onload = (e) => {
            // Update preview image source
            const img = this.previewTarget.querySelector('img')
            if (img) {
                img.src = e.target.result
                // Add opacity to indicate uploading
                img.style.opacity = '0.6'
            }
        }
        
        reader.readAsDataURL(file)
        
        // Update button text to indicate uploading
        if (this.hasSelectButtonTarget) {
            this.selectButtonTarget.textContent = "Uploading..."
            this.selectButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
        }
    }

    uploadFile(file) {
        // Show loading state on submit button if available
        if (this.hasSubmitButtonTarget) {
            const loadingButtonController = this.application.getControllerForElementAndIdentifier(
                this.submitButtonTarget, "loading-button"
            )
            if (loadingButtonController) {
                loadingButtonController.showLoading()
            }
        }

        // First compress the image
        ImageUploadHelper.compressImage(file, (error, compressedBlob, originalFileName) => {
            if (error) {
                this.handleUploadError(error)
                return
            }

            // Create DirectUpload
            const fileToUpload = new File([compressedBlob], originalFileName, { type: compressedBlob.type })
            const upload = new DirectUpload(fileToUpload, this.directUploadUrlValue)

            upload.create((error, blob) => {
                if (error) {
                    this.handleUploadError(error)
                } else {
                    this.handleUploadSuccess(blob)
                }
            })
        }, {
            maxWidth: 1024,
            maxHeight: 1024,
            quality: 0.8
        })
    }

    handleUploadSuccess(blob) {
        // Restore image opacity
        const img = this.previewTarget.querySelector('img')
        if (img) {
            img.style.opacity = '1'
        }

        // Update button text
        if (this.hasSelectButtonTarget) {
            this.selectButtonTarget.textContent = "Change photo"
            this.selectButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
        }

        // Show success message
        this.showSuccess("Photo updated")

        // Create hidden input with signed ID
        const hiddenInput = document.createElement("input")
        hiddenInput.type = "hidden"
        hiddenInput.name = "user[avatar]"
        hiddenInput.value = blob.signed_id
        
        // Remove any existing avatar inputs
        const existingInputs = this.element.querySelectorAll('input[name="user[avatar]"][type="hidden"]')
        existingInputs.forEach(input => input.remove())
        
        // Add new input
        this.element.appendChild(hiddenInput)

        // Stop loading state on submit button
        this.stopButtonLoading()
    }

    handleUploadError(error) {
        console.error("Upload failed:", error)
        
        // Restore original state
        const img = this.previewTarget.querySelector('img')
        if (img) {
            img.style.opacity = '1'
        }
        
        if (this.hasSelectButtonTarget) {
            this.selectButtonTarget.textContent = "Select photo"
            this.selectButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
        }
        
        this.showError("Failed to upload avatar. Please try again.")
        
        // Stop loading state
        this.stopButtonLoading()
        
        // Reset file input
        this.resetFileInput()
    }

    showSuccess(message) {
        if (this.hasStatusTarget) {
            this.statusTarget.textContent = message
            this.statusTarget.className = "text-xs text-warm-gray-500 mt-2"
            this.statusTarget.classList.remove("hidden")
            
            setTimeout(() => {
                this.statusTarget.classList.add("hidden")
            }, 5000)
        }
    }

    showError(message) {
        if (this.hasStatusTarget) {
            this.statusTarget.textContent = message
            this.statusTarget.className = "text-xs text-red-500 mt-2"
            this.statusTarget.classList.remove("hidden")
        }
    }

    resetFileInput() {
        this.fileInputTarget.value = ""
    }

    stopButtonLoading() {
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.dispatchEvent(new CustomEvent("loading-button:stop"))
        }
    }
}
